import React, { useEffect, useState } from "react";
import Modal from "../Modal";
import ConfirmationModal from "../ConfirmationModal";
import BaseButton from "./BaseButton";
import VisaCardImg from "../../../../images/card_visa.jpg";
import MasterCardImg from "../../../../images/card_mastercard.jpg";
import JCBCardImg from "../../../../images/card_jcb.jpg";
import AmexCardImg from "../../../../images/card_amex.jpg";
import DiscoverCardImg from "../../../../images/card_discover.jpg";
import DinersCoardImg from "../../../../images/card_diners.jpg";

type Props = {
  submitText: string;
  text: string;
  payJpPublicKey: string;
  callback: (token) => void;
  disabled?: () => boolean;
  isAddressEdited: boolean;
  postal_code: string;
  full_address: string;
  hideConfirmationModal?: boolean;
  accountEmail?: string;
  deliveryPhone?: string;
  hideButton?: boolean;
  openSignal?: number;
  onModalClose?: () => void;
};

declare global {
  interface Window {
    /* eslint-disable */
    Payjp: any;
    payjp: any;
    /* eslint-enable */
  }
}
const PaymentButton: React.FC<Props> = (props) => {
  const {
    submitText,
    text,
    payJpPublicKey,
    callback,
    disabled,
    postal_code,
    full_address,
    isAddressEdited,
    hideConfirmationModal = false,
    accountEmail = "",
    deliveryPhone = "",
    hideButton = false,
    openSignal = 0,
    onModalClose,
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [numberElement, setNumberElement] = useState<any>();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isAddressWarningModalActive, setIsAddressWarningModalActive] =
    useState<boolean>(false);
  const [brand, setBrand] = useState<string>("");
  const [cardName, setCardName] = useState<string>("");
  const [email, setEmail] = useState<string>(accountEmail);
  const [phone, setPhone] = useState<string>(deliveryPhone);
  const [isFormReady, setIsFormReady] = useState<boolean>(false);
  const [isCreatingToken, setIsCreatingToken] = useState<boolean>(false);
  const [tokenError, setTokenError] = useState<string>("");

  useEffect(() => {
    if (!isActive) return;

    let canceled = false;
    setNumberElement(undefined);
    setIsFormReady(false);
    setTokenError("");

    (async () => {
      try {
        const mountedNumberElement = await createForm();
        if (canceled) return;

        setNumberElement(mountedNumberElement);
        setIsFormReady(true);
      } catch (error) {
        console.error(error);
        if (canceled) return;

        setTokenError("カード入力欄の読み込みに失敗しました。再度お試しください。");
      }
    })();

    return () => {
      canceled = true;
    };
  }, [isActive]);

  useEffect(() => {
    if (openSignal === 0) return;

    openModal();
  }, [openSignal]);

  const createForm = async () => {
    if (window.payjp === undefined) {
      for (;;) {
        if (window.Payjp) {
          Object.defineProperty(window, "payjp", {
            value: window.Payjp(payJpPublicKey, {
              threeDSecureWorkflow: "iframe",
            }),
            writable: false,
          });
          break;
        } else {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }
    }

    const el = window.payjp.elements();
    const style = {
      base: {
        color: "#1f2122",
        "::placeholder": {
          color: "#b1c2cc",
        },
      },
      invalid: {
        color: "#ff0000",
      },
    };

    const numberEl = el.create("cardNumber", { style: style });
    const expiryEl = el.create("cardExpiry", { style: style });
    const cvcEl = el.create("cardCvc", { style: style });
    numberEl.mount("#number-form");
    expiryEl.mount("#expiry-form");
    cvcEl.mount("#cvc-form");

    numberEl.on("change", (event) => {
      const { brand } = event;

      setBrand(brand);
      updateOrInsertBrandIcon(brand);
    });

    return numberEl;
  };

  function openModal() {
    setIsActive(true);
  }

  function closeModal() {
    setIsActive(false);
    setNumberElement(undefined);
    setIsFormReady(false);
    setIsCreatingToken(false);
    onModalClose?.();
  }

  const toggleModal = () => {
    if (isActive) {
      closeModal();
    } else {
      openModal();
    }
  };

  const setToken = (token: string) => {
    callback(token);
    setIsActive(false);
  };

  const createToken = () => {
    if (!numberElement || isCreatingToken) return;

    setIsCreatingToken(true);
    setTokenError("");

    window.payjp
      .createToken(numberElement, {
        three_d_secure: true,
        card: {
          name: cardName,
          email: email,
          phone: "+81" + phone.replace("-", ""),
        },
      })
      .then(function (r) {
        if (r.error) {
          setTokenError(r.error.message);
        } else {
          setToken(r.id);
        }
      })
      .catch((error) => {
        console.error(error);
        setTokenError("カード情報の確認中にエラーが発生しました。再度お試しください。");
      })
      .finally(() => {
        setIsCreatingToken(false);
      });
  };

  const handleProceed = () => {
    if (hideConfirmationModal) {
      openCreateTokenModal(); // 直接処理を実行（モーダルを開かずに進む）
    } else {
      toggleAddressWarningModal(); // 通常通りモーダルを開く
    }
  };

  const toggleAddressWarningModal = () => {
    setIsAddressWarningModalActive(!isAddressWarningModalActive);
  };

  const openCreateTokenModal = () => {
    setIsAddressWarningModalActive(false);
    openModal();
  };

  const updateOrInsertBrandIcon = (brand) => {
    const numberForm = document.getElementById("number-form");
    if (!numberForm) return;

    const existingIcon = numberForm.querySelector("img");

    if (brand === "unknown") {
      if (existingIcon) {
        existingIcon.remove();
      }
    } else {
      if (existingIcon) {
        existingIcon.src = getBrandIconUrl(brand);
        existingIcon.alt = brand;
      } else {
        const brandIcon = document.createElement("img");
        brandIcon.src = getBrandIconUrl(brand);
        brandIcon.alt = brand;
        brandIcon.className = "w-10 h-[25px] mr-[10px]";
        numberForm.insertBefore(brandIcon, numberForm.firstChild);
      }
    }
  };

  const getBrandIconUrl = (brand) => {
    switch (brand) {
      case "Visa":
        return VisaCardImg;
      case "MasterCard":
        return MasterCardImg;
      case "JCB":
        return JCBCardImg;
      case "American Express":
        return AmexCardImg;
      case "Diners Club":
        return DinersCoardImg;
      case "Discover":
        return DiscoverCardImg;
      default:
        return null;
    }
  };

  return (
    <>
      {!hideButton && (
        <BaseButton
          text={text}
          onClick={isAddressEdited ? toggleModal : handleProceed}
          disabled={disabled ? disabled() : false}
        />
      )}
      {!hideConfirmationModal && (
        <ConfirmationModal
          toggleModal={toggleAddressWarningModal}
          onSubmit={openCreateTokenModal}
          isActive={isAddressWarningModalActive}
          confirmText="進む"
        >
          <div className="leading-[1.875]">
            <p className="mb-5">
              確実にリターン品をお届けするため、住所をご確認ください。
            </p>
            <p className="mb-1">〒{postal_code}</p>
            <p className="mb-5">{full_address}</p>
          </div>
        </ConfirmationModal>
      )}
      <Modal isActive={isActive} toggleModal={closeModal}>
        {isActive && (
          <>
            <ul className="flex mb-5">
              <li className="h-[25px] mr-[10px] w-10">
                <img src={VisaCardImg} alt="visaカード" />
              </li>
              <li className="h-[25px] mr-[10px] w-10">
                <img src={MasterCardImg} alt="masterカード" />
              </li>
              <li className="h-[25px] mr-[10px] w-10">
                <img src={JCBCardImg} alt="jcbカード" />
              </li>
              <li className="h-[25px] mr-[10px] w-10">
                <img src={AmexCardImg} alt="american expressカード" />
              </li>
              <li className="h-[25px] mr-[10px] w-10">
                <img src={DiscoverCardImg} alt="discoverカード" />
              </li>
            </ul>
            <div>
              <label htmlFor="name-form" className="w-[100px] mr-5 text-sm">
                カード名義
              </label>
              <div
                id="name-form"
                className="payjs-outer border-[#B1C2CC] py-[15px] flex items-center mb-5 px-5 border"
              >
                <input
                  type="text"
                  className="h-[20px] text-md text-[#1f2122] w-full outline-none"
                  placeholder="カード名義"
                  onChange={(e) => setCardName(e.target.value)}
                />
              </div>
              <label htmlFor="number-form" className="w-[100px] mr-5 text-sm">
                カード番号
              </label>
              <div
                id="number-form"
                className="payjs-outer border-[#B1C2CC] py-[15px] flex items-center mb-5 px-5 border"
              ></div>
              <label htmlFor="expiry-form" className="w-[100px] mr-5 text-sm">
                有効期限
              </label>
              <div
                id="expiry-form"
                className="payjs-outer border-[#B1C2CC] py-[15px] mb-5 px-5 border"
              ></div>
              <label htmlFor="cvc-form" className="w-[100px] mr-5 text-sm">
                CVC番号
              </label>
              <div
                id="cvc-form"
                className="payjs-outer border-[#B1C2CC] py-[15px] mb-5 px-5 border"
              ></div>
              {accountEmail === "" && (
                <>
                  <label
                    htmlFor="email-form"
                    className="w-[100px] mr-5 text-sm"
                  >
                    メールアドレス
                  </label>
                  <div
                    id="email-form"
                    className="payjs-outer border-[#B1C2CC] py-[15px] flex items-center mb-5 px-5 border"
                  >
                    <input
                      type="text"
                      className="h-[20px] text-md text-[#1f2122] w-full outline-none"
                      placeholder="メールアドレス"
                      onChange={(e) => setEmail(e.target.value)}
                      value={email}
                    />
                  </div>
                </>
              )}
              <label htmlFor="phone-form" className="w-[100px] mr-5 text-sm">
                電話番号
              </label>
              <div
                id="phone-form"
                className="payjs-outer border-[#B1C2CC] py-[15px] flex items-center mb-5 px-5 border"
              >
                <input
                  type="text"
                  className="h-[20px] text-md text-[#1f2122] w-full outline-none"
                  placeholder="電話番号"
                  onChange={(e) => setPhone(e.target.value)}
                  value={phone}
                />
              </div>
            </div>
            <div className="mt-10">
              <span
                id="token"
                className="text-[#FF0000] mb-[10px] block text-center text-xs"
              >
                {tokenError}
              </span>
              <div className="w-[250px] h-[50px] mx-auto">
                <BaseButton
                  onClick={createToken}
                  text={submitText}
                  disabled={!isFormReady || isCreatingToken}
                />
              </div>
              <p className="text-[#FF0000] mt-3 text-center text-xs">
                カード有効性確認のため、11円のカード利用速報等が通知される場合がありますが、実際に決済は行なっておりません。
              </p>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default PaymentButton;
